/**
 * Поведение страницы входа ЛК при единой авторизации (CAB-638, Ф1–Ф2).
 *
 * Формы входа у ЛК нет: её отдаёт REG на своём домене. Здесь остаются три решения —
 * куда вернуть человека после входа, уводить ли сразу и что показать, если уводить
 * оказалось некуда.
 *
 * Адрес страницы входа спрашивается у бэкенда, а не берётся из сборки: контур знает
 * только он. Спрашиваем дважды и по разным поводам — сначала чтобы показать домен
 * рядом с кнопкой (по нему человек отличает фишинг), потом чтобы уйти. Второе сервер
 * засчитывает как попытку, первое — нет.
 */

import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { fetchLoginUrl, recoverSession } from "../../api/authEntryApi";
import { redirectToAuthEntry } from "../helpers/auth/authBrowserState";
import { sanitizeInternalPath } from "../helpers/auth/unifiedAuth";

const HOME_PATH = "/";

/** Гард ставит этот признак, когда человек вернулся со страницы входа без сессии. */
export const SESSION_BLOCKED_QUERY = "blocked";

/** Гард ставит его тому, кто уже входил в этом браузере: уводим без лишнего клика. */
export const AUTO_ENTRY_QUERY = "auto";

export function useAuthEntry() {
  const route = useRoute();
  const router = useRouter();

  const leaving = ref(false);

  /**
   * Уйти не удалось. Причина всегда одна из двух — сервер не знает адреса своего
   * контура, либо это уже вторая попытка подряд и сработала анти-петля. Разделять их
   * на экране незачем: действие человека в обоих случаях одинаковое.
   */
  const entryUnavailable = ref(false);

  const authHost = ref("");

  /**
   * Подсказка, а не приговор: показываем, что сессия не сохранилась, но оставляем
   * обычный вход первым действием. Тупиковый экран здесь был бы хуже — человек,
   * который просто нажал «назад» на странице входа, увидел бы сообщение об ошибке,
   * которой не было.
   */
  const showSessionNotice = computed(() => route.query.session === SESSION_BLOCKED_QUERY);

  /** Внутренний адрес возврата. Чужой адрес из `?redirect=` не принимаем. */
  const returnPath = computed(() => sanitizeInternalPath(route.query.redirect) ?? HOME_PATH);

  async function goToAuth(): Promise<void> {
    if (leaving.value) return;

    leaving.value = true;
    entryUnavailable.value = false;

    const left = await redirectToAuthEntry(returnPath.value);

    if (!left) {
      entryUnavailable.value = true;
      leaving.value = false;
    }
    // При успехе `leaving` не снимаем: страница уже уходит с домена, и возврат кнопки
    // в исходное состояние был бы мельканием на прощание
  }

  onMounted(async () => {
    if (route.query[AUTO_ENTRY_QUERY] === "1") {
      // Прежде чем уводить за паролем, спрашиваем сервер, не жива ли сессия ещё
      // (CAB-690). Куки eplex_token может не быть просто потому, что браузер удалил
      // её по сроку, пока кабинет стоял открытым, — а refresh при этом годен ещё
      // четверо суток. Уводить такого человека вводить пароль незачем.
      if (await recoverSession()) {
        await router.replace(returnPath.value);
        return;
      }

      await goToAuth();
      return;
    }

    // Домен для подсказки спрашиваем БЕЗ намерения уйти, иначе анти-петля засчитает
    // это как попытку и обычный вход покажет «войти не удалось»
    const loginUrl = await fetchLoginUrl(returnPath.value);

    if (loginUrl === null) {
      entryUnavailable.value = true;
      return;
    }

    try {
      authHost.value = new URL(loginUrl).host;
    } catch {
      authHost.value = "";
    }
  });

  return { leaving, showSessionNotice, returnPath, goToAuth, authHost, entryUnavailable };
}
